import type { JSX } from "solid-js";

/**
 * Inline SVG illustrations for empty states / headers.
 *
 * All are theme-aware: they use `currentColor` and opacities, inheriting
 * the container color (typically `text-primary`). Decorative by
 * default (`aria-hidden`), empty state text provides semantics.
 */

type IllustrationProps = JSX.SvgSVGAttributes<SVGSVGElement>;

const baseProps = (props: IllustrationProps) => ({
  viewBox: "0 0 160 120",
  fill: "none",
  "aria-hidden": "true" as const,
  ...props,
});

/** Garage with vehicle: empty state for "no assets". */
export function AssetsIllustration(props: IllustrationProps): JSX.Element {
  return (
    <svg {...baseProps(props)} xmlns="http://www.w3.org/2000/svg">
      {/* Ground */}
      <line
        x1="20"
        y1="98"
        x2="140"
        y2="98"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        opacity="0.25"
      />
      {/* Car body */}
      <path
        d="M38 84c0-4 2-6 6-6h2l8-14c2-3.5 5-5.5 9-5.5h30c4 0 7 1.8 9.6 5l9.4 12 9 2.5c4 1.1 6 3.4 6 7.5V84"
        fill="currentColor"
        opacity="0.12"
      />
      <path
        d="M38 84c0-4 2-6 6-6h2l8-14c2-3.5 5-5.5 9-5.5h30c4 0 7 1.8 9.6 5l9.4 12 9 2.5c4 1.1 6 3.4 6 7.5V84"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linejoin="round"
      />
      {/* Window */}
      <path
        d="M58 64c1.4-2.4 3-3.6 5.4-3.6H88c2.4 0 4.2 1 5.8 3l6 8H53.6L58 64Z"
        fill="currentColor"
        opacity="0.18"
      />
      {/* Wheels */}
      <circle cx="56" cy="86" r="9" fill="currentColor" opacity="0.14" />
      <circle cx="56" cy="86" r="9" stroke="currentColor" stroke-width="2.5" />
      <circle cx="56" cy="86" r="3" fill="currentColor" />
      <circle cx="110" cy="86" r="9" fill="currentColor" opacity="0.14" />
      <circle cx="110" cy="86" r="9" stroke="currentColor" stroke-width="2.5" />
      <circle cx="110" cy="86" r="3" fill="currentColor" />
      {/* Sparkle / plus */}
      <path
        d="M126 40v12M120 46h12"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        opacity="0.6"
      />
      <circle cx="40" cy="36" r="2.5" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

/** Wrench + gear: empty state for "no services/maintenance". */
export function ServiceIllustration(props: IllustrationProps): JSX.Element {
  return (
    <svg {...baseProps(props)} xmlns="http://www.w3.org/2000/svg">
      {/* Gear */}
      <circle cx="64" cy="60" r="24" fill="currentColor" opacity="0.1" />
      <path
        d="M64 34v-7m0 66v-7m26-26h7M34 60h-7m44.4-18.4 5-5M41.6 78.4l-5 5m41.8 0-5-5M41.6 41.6l-5-5"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        opacity="0.55"
      />
      <circle cx="64" cy="60" r="24" stroke="currentColor" stroke-width="2.5" />
      <circle cx="64" cy="60" r="9" fill="currentColor" opacity="0.16" />
      <circle cx="64" cy="60" r="9" stroke="currentColor" stroke-width="2.5" />
      {/* Wrench */}
      <path
        d="M104 40a11 11 0 0 0-13.6 13.6L78 66l8 8 12.4-12.4A11 11 0 0 0 112 48l-7 7-5-5 4-10Z"
        fill="currentColor"
        opacity="0.14"
      />
      <path
        d="M104 40a11 11 0 0 0-13.6 13.6L78 66l8 8 12.4-12.4A11 11 0 0 0 112 48l-7 7-5-5 4-10Z"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linejoin="round"
      />
    </svg>
  );
}

/** Calendar with checkmark: empty state for "nothing scheduled". */
export function ScheduleIllustration(props: IllustrationProps): JSX.Element {
  return (
    <svg {...baseProps(props)} xmlns="http://www.w3.org/2000/svg">
      <rect x="44" y="40" width="72" height="62" rx="8" fill="currentColor" opacity="0.1" />
      <rect x="44" y="40" width="72" height="62" rx="8" stroke="currentColor" stroke-width="2.5" />
      <path d="M44 56h72" stroke="currentColor" stroke-width="2.5" />
      <path
        d="M58 34v12M102 34v12"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
      />
      {/* Check */}
      <path
        d="M64 80l9 9 17-19"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <circle cx="124" cy="44" r="2.5" fill="currentColor" opacity="0.4" />
    </svg>
  );
}
